import React from "react";
import { motion } from "framer-motion";
import { FiCheckCircle } from "react-icons/fi";
import SectionHeading from "../../components/SectionHeading";
import { leadershipContent } from "./teamContent";
import "./TeamLeadership.css";

/* ============================================================================
   "Our Leadership Team" — the document's four leaders.

   One full profile per leader in the document's order, carrying every line of
   copy the document gives that person. (The organization chart that stood
   above the profiles was removed on the user's instruction, 2026-09-25.)

   Photographs are the project's own. The two leaders the project holds no
   photograph for show their initials rather than a stand-in picture.
   ========================================================================= */

const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.5, ease: "easeOut" },
};

const Avatar = ({ member, size }) =>
  member.image ? (
    <img
      src={member.image}
      alt={`${member.name}, ${member.designation}`}
      loading="lazy"
      className={`leader-avatar leader-avatar--${size}`}
    />
  ) : (
    <span
      className={`leader-avatar leader-avatar--${size} leader-avatar--initials`}
      role="img"
      aria-label={`${member.name}, ${member.designation}`}
    >
      {member.initials}
    </span>
  );

const CheckList = ({ items }) => (
  <ul className="leader-checks">
    {items.map((item) => (
      <li key={item}>
        <FiCheckCircle aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Profile = ({ member, featured }) => (
  <motion.article
    {...reveal}
    id={member.id}
    className={`leader-profile brand-card ${featured ? "leader-profile--featured" : ""}`}
    aria-labelledby={`${member.id}-name`}
  >
    <div className="leader-profile__aside">
      <Avatar member={member} size="lg" />
      <h3 id={`${member.id}-name`} className="leader-profile__name">
        {member.name}
      </h3>
      <p className="leader-profile__title">{member.designation}</p>
    </div>

    <div className="leader-profile__body">
      {member.paragraphs.map((p) => (
        <p key={p} className="leader-profile__para">
          {p}
        </p>
      ))}

      {member.expertiseIntro && (
        <p className="leader-profile__lead">{member.expertiseIntro}</p>
      )}

      {member.expertise && (
        <div
          className={`leader-groups ${
            member.expertise.length > 1 ? "leader-groups--split" : ""
          }`}
        >
          {member.expertise.map((group) => (
            <div key={group.title} className="leader-group">
              <h4 className="leader-group__title">{group.title}</h4>
              <CheckList items={group.items} />
            </div>
          ))}
        </div>
      )}

      {member.skills && (
        <div className="leader-skills">
          <h4 className="leader-skills__title">{member.skillsTitle}</h4>
          <div className="leader-groups leader-groups--split">
            {member.skills.map((group) => (
              <div key={group.title} className="leader-group">
                <h5 className="leader-group__title">{group.title}</h5>
                <CheckList items={group.items} />
              </div>
            ))}
          </div>
        </div>
      )}

      {member.closing && (
        <p className="leader-profile__closing">{member.closing}</p>
      )}

      {/* the existing introduction video, kept with its subject */}
      {member.video && (
        <video
          src={member.video}
          className="leader-profile__video"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-label={`${member.name} introduction video`}
        />
      )}
    </div>
  </motion.article>
);

const TeamLeadership = () => (
  <section
    id="leadership-team"
    className="leadership bg-white py-14 lg:py-20"
    aria-labelledby="leadership-heading"
  >
    <div className="brand-col">
      <SectionHeading id="leadership-heading" title={leadershipContent.title} />

      {/* Profiles, in the document's order */}
      <div className="leader-profiles">
        {leadershipContent.members.map((member, i) => (
          <Profile key={member.id} member={member} featured={i === 0} />
        ))}
      </div>
    </div>
  </section>
);

export default TeamLeadership;
